import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { TrackableEvent } from './types';
import { buildEventTrackingContext, injectGtm, removeGtm, trackPageView } from './gtm';
import { isGtmAllowedPath } from './gtmAllowedRoutes';
import { clearGtmTrackingContext, setGtmTrackingContext } from './gtmTrackingContext';

export function useEventGtm(event: TrackableEvent | null | undefined): void {
  const location = useLocation();
  const isAllowedPath = isGtmAllowedPath(location.pathname);

  useEffect(() => {
    if (!event) {
      clearGtmTrackingContext();
      return;
    }

    setGtmTrackingContext({
      eventTitle: event.title,
      eventSlug: event.slug,
      gtmContainerId: event.gtm_container_id,
    });

    return () => {
      clearGtmTrackingContext();
    };
  }, [event?.id, event?.title, event?.slug, event?.gtm_container_id]);

  useEffect(() => {
    if (!event?.gtm_container_id || !isAllowedPath) {
      removeGtm();
      return;
    }

    injectGtm(event.gtm_container_id);

    return () => {
      removeGtm();
    };
  }, [event?.gtm_container_id, event?.id, isAllowedPath]);

  useEffect(() => {
    if (!event?.gtm_container_id || !isAllowedPath) {
      return;
    }

    trackPageView(buildEventTrackingContext(event), location.pathname, document.title);
  }, [event?.gtm_container_id, event?.id, event?.slug, event?.title, location.pathname, isAllowedPath]);
}
